import { getJwtString } from '@backoffice-frontend/authentication'
import {
  ENV,
  Environment,
  mockServerPort,
} from '@backoffice-frontend/environment'
import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import cube from '@cubejs-client/core'
import { CubeProvider as Provider } from '@cubejs-client/react'
import { type ReactNode, useMemo } from 'react'
import { InstrumentedHttpTransport } from './InstrumentedHttpTransport'
import {
  type CubeRetryConfig,
  DEFAULT_CUBE_RETRY_CONFIG,
  RetryingHttpTransport,
} from './RetryingHttpTransport'

// Local Cube is served by the mock server by default, so selecting "local"
// loads offline fixtures — consistent with how GraphQL is mock-served (see
// GraphQLStages in endpoints.ts). To point at a locally-running Cube repo
// (e.g. http://localhost:4000) instead, set VITE_CUBE_ENDPOINT_OVERRIDE — no
// rebuild needed, same escape hatch as VITE_GRAPHQL_ENDPOINT_OVERRIDE.
const localCubeBaseUrl =
  __VITE_CUBE_ENDPOINT_OVERRIDE__ || `http://localhost:${mockServerPort}`

// API endpoints
const backofficeDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/cubejs-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://backoffice-data-api.insights.dev.moia-group.io/cubejs-api/v1',
  [Environment.integration]:
    'https://backoffice-data-api.insights.int.moia-group.io/cubejs-api/v1',
  [Environment.production]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
  [Environment['production.us-east-1']]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
}
const backofficeMultitenantDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/multi-tenant-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://multi-tenant-cube-data-api.insights.dev.moia-group.io/multi-tenant-api/v1',
  [Environment.integration]:
    'https://multi-tenant-cube-data-api.insights.int.moia-group.io/multi-tenant-api/v1',
  [Environment.production]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
  [Environment['production.us-east-1']]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
}
const dapDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: `${localCubeBaseUrl}/multi-tenant-api/v1`,
  [Environment.test]: '',
  [Environment.development]:
    'https://cube-api.dev.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment.integration]:
    'https://cube-api.int.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment.production]:
    'https://cube-api.prd.eu-central-1.api.moia-group.io/multi-tenant-api/v1',
  [Environment['production.us-east-1']]:
    'https://cube-api.prd.us-east-1.api.moia-group.io/multi-tenant-api/v1',
}

const getToken = async () => {
  const token = await getJwtString()
  return token ?? ''
}

type CubeApiSet = {
  regular: ReturnType<typeof cube>
  multitenant: ReturnType<typeof cube>
  dap: ReturnType<typeof cube>
}

// `retryConfig` undefined → plain instrumented transport (current behavior).
// Defined → retrying transport that transparently retries transient failures.
const createCubeApis = (retryConfig?: CubeRetryConfig): CubeApiSet => {
  const makeTransport = (apiUrl: string) =>
    retryConfig
      ? new RetryingHttpTransport({ apiUrl }, retryConfig)
      : new InstrumentedHttpTransport({ apiUrl })

  return {
    regular: cube(getToken, {
      apiUrl: backofficeDataApiEndpoints[ENV],
      transport: makeTransport(backofficeDataApiEndpoints[ENV]),
    }),
    multitenant: cube(getToken, {
      apiUrl: backofficeMultitenantDataApiEndpoints[ENV],
      transport: makeTransport(backofficeMultitenantDataApiEndpoints[ENV]),
    }),
    dap: cube(getToken, {
      apiUrl: dapDataApiEndpoints[ENV],
      transport: makeTransport(dapDataApiEndpoints[ENV]),
    }),
  }
}

// Default (non-retrying) APIs are module-level singletons, unchanged.
const defaultCubeApis = createCubeApis()

// Simple provider that allows switching between APIs
export const CubeProvider = ({
  children,
  useMultitenantCubeApi = false,
  retry = false,
}: {
  children: ReactNode
  useMultitenantCubeApi?: boolean
  /**
   * Transparently retry transient Cube failures (5xx / network / timeout) with
   * a backoff schedule. `true` uses the default (immediate, +2s, +5s); pass a
   * {@link CubeRetryConfig} to customize. Opt-in — consumers that omit it keep
   * the current no-retry behavior.
   */
  retry?: boolean | CubeRetryConfig
}) => {
  const useDapEndpoint = useIsFeatureToggleEnabled(
    Features.DAP_CUBE_ENDPOINT_V2,
  )

  const retryConfig = retry
    ? retry === true
      ? DEFAULT_CUBE_RETRY_CONFIG
      : retry
    : undefined

  // Reuse the singletons when retry is off; build retrying APIs once per config.
  const apis = useMemo(
    () => (retryConfig ? createCubeApis(retryConfig) : defaultCubeApis),
    [retryConfig],
  )

  const selectedApi = useDapEndpoint
    ? apis.dap
    : useMultitenantCubeApi
      ? apis.multitenant
      : apis.regular

  return <Provider cubeApi={selectedApi}>{children}</Provider>
}
